import { useEffect, useState } from 'react'
import { isMainWindow } from './lib/appWindows'
import { subscribeToPush } from './lib/push'
import { useSessionRenewal } from './lib/sessionRenewal'
import { Navigate, Route, Routes } from 'react-router-dom'
import { getSession, onSessionChange } from './lib/auth'
import { realtime } from './lib/realtime'
import { useDesktopLinks } from './lib/useDesktopLinks'
import Shell from './components/Shell'
import SignIn from './pages/SignIn'
import AcceptInvite from './pages/AcceptInvite'
import Handoff from './pages/Handoff'
import Dashboard from './pages/Dashboard'
import InboxPage from './pages/InboxPage'
import Floor from './pages/Floor'
import KidsHome from './pages/KidsHome'
import Start from './pages/Start'
import DesktopSignIn from './pages/DesktopSignIn'
import { forgetAfterSignIn, peekAfterSignIn } from './lib/afterSignIn'
import { acceptSignedInLink } from './lib/desktopSignIn'
import { onOpenUrl } from './lib/desktop'
import BugHunt from './pages/BugHunt'
import WorkSession from './pages/WorkSession'
import Tasks from './pages/Tasks'
import Specs from './pages/Specs'
import Sessions from './pages/Sessions'
import Activity from './pages/Activity'
import DevDay, { MyDay } from './pages/DevDay'
import Remote from './pages/Remote'
import MyCharacter from './pages/MyCharacter'
import Security from './pages/Security'
import Usage from './pages/Usage'
import Reports from './pages/Reports'
import Docs from './pages/Docs'
import PinnedInstructions from './pages/PinnedInstructions'
import Editor from './pages/Editor'
import Repositories from './pages/Repositories'
import WorkOrders from './pages/WorkOrders'
import ConnectAgent from './pages/ConnectAgent'
import Skills from './pages/Skills'
import Prompts from './pages/Prompts'
import Audit from './pages/Audit'
import StartProject from './pages/StartProject'
import { pokeRunner, startRunnerIfOn, warmPinned } from './lib/runnerHost'
import { useDesktopAutoRunner } from './lib/desktopAutoRunner'
import { startAnalytics } from './lib/analytics'
import Settings from './pages/Settings'
import Plugins from './pages/Plugins'
import Contact from './pages/Contact'
import Terms from './pages/Terms'
import Privacy from './pages/Privacy'
import ResetPassword from './pages/ResetPassword'
import { AccountNotices } from './components/AccountData'
import Admin from './pages/Admin'
import { isAdminHost } from './lib/adminHost'
import { startInventoryReports } from './lib/machineInventory'
import { startCli } from './lib/projectWindows'
import { allFolders, allFoldersByRepository } from './lib/repoFolders'
import { AgentsConversations, Moved, ProjectOverview, Space, SpaceIndex } from './pages/Space'
import { MOVED_ROUTES } from './lib/navigation'

export default function App() {
  const [session, setSession] = useState(getSession)

  // Product analytics, when a service is configured for this build (off by default).
  useEffect(() => startAnalytics(), [])

  useEffect(() => onSessionChange(() => setSession(getSession())), [])

  // The session renews itself before it expires, so an app left open is not signed out every 12 hours (docs/refactor/28).
  useSessionRenewal(Boolean(session))

  // Notifications to the phone (docs/refactor/28): a browser that allowed notifications subscribes to the server's pushes.
  const signedIn = Boolean(session)
  useEffect(() => {
    if (signedIn) void subscribeToPush()
  }, [signedIn])

  // The desktop at home keeps taking work whether or not anybody is signed in on its screen: the runner works with the
  // key in the system's vault, not with the person's session (docs/refactor/28).
  // Only the main window: another window is a view on its own project, not a second runner (lib/appWindows.ts).
  useEffect(() => {
    if (isMainWindow()) startRunnerIfOn()
  }, [])

  // The desktop app signed in through the browser (Google): the website hands back
  // devpulse://signed-in, taken only with the state this app is waiting for (lib/desktopSignIn.ts).
  useEffect(() => {
    let stop: (() => void) | undefined
    let dropped = false
    void onOpenUrl((urls) => {
      for (const url of urls) void acceptSignedInLink(url).catch(() => {})
    }).then((off) => {
      if (dropped) off()
      else stop = off
    })
    return () => {
      dropped = true
      stop?.()
    }
  }, [])

  // One connection for the whole signed-in application, opened once and closed on sign-out.
  // Opening it per page would reconnect on every navigation and lose buffered updates each time.
  useEffect(() => {
    if (!session) {
      void realtime.stop()
      return
    }

    void realtime.start()
    return () => {
      void realtime.stop()
    }
  }, [session])

  // The platform's admin area: its own sign-in, on the admin subdomain, whether or not anybody is signed in here.
  if (isAdminHost() && (window.location.hostname.startsWith('devadmin.') || window.location.pathname.startsWith('/admin'))) {
    return <Admin />
  }

  if (!session) {
    return (
      <Routes>
        <Route path="/contact" element={<Contact standalone />} />
        {/* The Terms of Use, readable before anybody has an account. */}
        <Route path="/terms" element={<Terms standalone />} />
        <Route path="/privacy" element={<Privacy standalone />} />
        {/* Where the "Esqueci a senha" email lands. */}
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/sign-in" element={<SignIn />} />
        {/* Reachable without an account, which is the entire point of an invitation. */}
        <Route path="/invite/:token" element={<AcceptInvite />} />
        {/* "View on the web" from the desktop app: signs this browser in with a one-time code. */}
        <Route path="/auth/handoff" element={<Handoff />} />
        {/* The desktop app signing in through the browser (Google): sign in here, then back to it. */}
        <Route path="/auth/desktop" element={<DesktopSignIn />} />
        <Route path="*" element={<Navigate to="/sign-in" replace />} />
      </Routes>
    )
  }

  return (
    <Shell>
      {/* devpulse:// links opened elsewhere on the machine land on a page. Nothing in a browser. */}
      <DesktopLinks />
      {/* "Your account will be deleted on …" and "welcome back" (docs/refactor/65). */}
      <AccountNotices />
      <Routes>
        {/* Opening the app: the Engineering Floor, or Conectar agente while no agent is connected. */}
        <Route path="/" element={<Start />} />
        {/* Kids mode (docs/refactor/48): the start of a child's Vephus. */}
        <Route path="/kids" element={<KidsHome />} />
        {/* The same, for the web: there nginx serves the landing page at "/". */}
        <Route path="/start" element={<Start />} />
        <Route path="/dashboard" element={<Dashboard />} />
        <Route path="/inbox" element={<InboxPage />} />
        {/* Projeto and Acompanhar: one screen each, with tabs (docs/refactor/05-navegacao.md). */}
        <Route path="/project" element={<Space area="project" />}>
          <Route index element={<SpaceIndex area="project" />} />
          <Route path="overview" element={<ProjectOverview />} />
          <Route path="docs" element={<Docs />} />
          <Route path="pinned" element={<PinnedInstructions />} />
          <Route path="specs" element={<Specs />} />
          <Route path="tasks" element={<Tasks />} />
          <Route path="quality" element={<Audit />} />
          <Route path="quality/security" element={<Security />} />
          <Route path="editor" element={<Editor />} />
        </Route>
        <Route path="/agents" element={<Space area="agents" />}>
          <Route index element={<SpaceIndex area="agents" />} />
          <Route path="chat" element={<AgentsConversations />} />
          <Route path="work" element={<WorkOrders />} />
          <Route path="remote" element={<Remote />} />
        </Route>
        {/* Setting an agent up lives in Configurações (41, A7); the routes stay, so old links still land. */}
        <Route path="/agents/connect" element={<ConnectAgent />} />
        <Route path="/agents/skills" element={<Skills />} />
        <Route path="/track" element={<Space area="track" />}>
          <Route index element={<SpaceIndex area="track" />} />
          <Route path="floor" element={<Floor />} />
          <Route path="bugs" element={<BugHunt />} />
          <Route path="list" element={<Activity />} />
          <Route path="sessions" element={<Sessions />} />
          <Route path="reports" element={<Reports />} />
          <Route path="usage" element={<Usage />} />
          <Route path="day" element={<MyDay />} />
        </Route>
        {/* The routes from before the tabs: bookmarks, notifications and documents still land right. */}
        {Object.entries(MOVED_ROUTES).map(([from, to]) => (
          <Route key={from} path={from} element={<Moved to={to} />} />
        ))}
        <Route path="/work/:id" element={<WorkSession />} />
        <Route path="/developers/:id/day" element={<DevDay />} />
        <Route path="/me/character" element={<MyCharacter />} />
        <Route path="/agents/repositories" element={<Repositories />} />
        <Route path="/agents/prompts" element={<Prompts />} />
        <Route path="/agents/plugins" element={<Plugins />} />
        <Route path="/contact" element={<Contact />} />
        <Route path="/terms" element={<Terms />} />
        <Route path="/privacy" element={<Privacy />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/start-project" element={<StartProject />} />
        <Route path="/settings/*" element={<Settings />} />
        {/* The IDE tab of Agentes › Conectar agente; old links still land there. */}
        <Route path="/onboarding" element={<Navigate to="/agents/connect?tab=ide" replace />} />
        {/* Somebody already signed in may still be following an invitation to another
            organization, so the page is reachable either way. */}
        <Route path="/invite/:token" element={<AcceptInvite />} />
        <Route path="/auth/handoff" element={<Handoff />} />
        <Route path="/auth/desktop" element={<DesktopSignIn />} />
        <Route path="/sign-in" element={<AfterSignIn />} />
        <Route path="*" element={<Navigate to="/track/floor" replace />} />
      </Routes>
    </Shell>
  )
}

/** The hook needs to be inside the router, and a hook cannot be called from JSX. */
function DesktopLinks() {
  return isMainWindow() ? <MainWindowWork /> : null
}

/** What the desktop does once per computer: in the main window only (lib/appWindows.ts). */
function MainWindowWork() {
  useDesktopLinks()
  // …and switches itself on with a key, an agent and the projects open here (lib/desktopAutoRunner).
  useDesktopAutoRunner()
  // What this computer has installed, told to Vephus for the web (docs/refactor/14-skills-e-mcps.md).
  useEffect(() => startInventoryReports(allFolders), [])
  // `vephus --project … --prompt …` from the command line (docs/refactor/29).
  useEffect(() => startCli(allFoldersByRepository), [])
  // A command sent from elsewhere starts at once, not at the runner's next round; the pinned
  // instructions are fetched ahead of the first prompt run here.
  useEffect(() => {
    warmPinned()
    return realtime.onPromptQueued(() => pokeRunner())
  }, [])
  return null
}

/** Just signed in: where the page that asked for it wanted to go (once), or the start page. */
function AfterSignIn() {
  const [to] = useState(peekAfterSignIn)
  useEffect(() => forgetAfterSignIn(), [])
  return <Navigate to={to} replace />
}
